import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { usePaymentSettingsStore } from '../stores/paymentSettingsStore';
import QRCode from 'qrcode';

// shadcn/ui components
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from './ui/card';
import { Alert, AlertDescription } from './ui/alert';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';

// Lucide React icons
import { QrCode, RefreshCw, Info, Eye, EyeOff, Upload, X } from 'lucide-react';

const PaymentSettings: React.FC = () => {
  const { translate } = useTranslation();
  const { settings, setSettings, resetToDefault } = usePaymentSettingsStore();
  const [previewQR, setPreviewQR] = useState<string>('');
  const [showQRPreview, setShowQRPreview] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(
    settings.qrCodeImage || null
  );

  // Sync image preview with settings
  useEffect(() => {
    setImagePreview(settings.qrCodeImage || null);
  }, [settings.qrCodeImage]);

  const handleSettingChange = (key: keyof typeof settings, value: any) => {
    setSettings({ [key]: value });
  };

  const handleReset = () => {
    resetToDefault();
    setPreviewQR('');
    setShowQRPreview(false);
    setImagePreview(null);
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert(
        translate(
          'pos.settings.payment.invalidImageType',
          'Please upload an image file'
        )
      );
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert(
        translate(
          'pos.settings.payment.imageTooLarge',
          'Image size must be less than 2MB'
        )
      );
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setImagePreview(result);
      handleSettingChange('qrCodeImage', result);
      // Clear text data when image is uploaded
      handleSettingChange('qrCodeData', '');
    };
    reader.onerror = () => {
      alert(
        translate(
          'pos.settings.payment.imageUploadError',
          'Failed to read image file'
        )
      );
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveImage = () => {
    setImagePreview(null);
    handleSettingChange('qrCodeImage', undefined);
  };

  const generateQRPreview = async () => {
    // If image is uploaded, show it
    if (imagePreview) {
      setPreviewQR('');
      setShowQRPreview(true);
      return;
    }

    // Otherwise generate from text data
    if (!settings.qrCodeData.trim()) {
      return;
    }

    try {
      setIsGenerating(true);
      const qrCodeSVG = await QRCode.toString(settings.qrCodeData, {
        type: 'svg',
        width: 200,
        margin: 2,
      });
      setPreviewQR(qrCodeSVG);
      setShowQRPreview(true);
    } catch (error) {
      console.error('Error generating QR code:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <QrCode className="h-6 w-6 text-primary" />
            <div>
              <CardTitle>
                {translate('pos.settings.payment.title', 'Payment Settings')}
              </CardTitle>
              <CardDescription className="mt-1">
                {translate(
                  'pos.settings.payment.subtitle',
                  'Configure payment QR code for receipts'
                )}
              </CardDescription>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleReset}>
            <RefreshCw className="h-4 w-4 mr-2" />
            {translate('pos.settings.payment.reset', 'Reset')}
          </Button>
        </div>
      </CardHeader>

      <CardContent className="space-y-6">
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            {translate(
              'pos.settings.payment.info',
              'Configure a payment QR code that will be included in printed receipts. You can either upload an existing QR code image or enter payment information to generate one automatically.'
            )}
          </AlertDescription>
        </Alert>

        {/* QR Code Image Upload */}
        <div className="space-y-2">
          <Label>
            {translate(
              'pos.settings.payment.uploadQRImage',
              'Upload QR Code Image'
            )}
          </Label>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="cursor-pointer"
              />
            </div>
            {imagePreview && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleRemoveImage}
              >
                <X className="h-4 w-4 mr-2" />
                {translate('pos.settings.payment.removeImage', 'Remove')}
              </Button>
            )}
          </div>
          {imagePreview && (
            <div className="mt-2 border rounded-lg p-4 bg-muted/50">
              <div className="flex flex-col items-center space-y-2">
                {settings.qrCodeLabel && (
                  <p className="text-sm font-medium">{settings.qrCodeLabel}</p>
                )}
                <img
                  src={imagePreview}
                  alt="QR Code Preview"
                  className="max-w-[200px] max-h-[200px] bg-white p-2 rounded"
                  style={{ filter: 'grayscale(100%)' }}
                />
              </div>
            </div>
          )}
          <p className="text-sm text-muted-foreground">
            {translate(
              'pos.settings.payment.uploadQRImageHelp',
              'Upload an existing QR code image. If you upload an image, the text data below will be ignored.'
            )}
          </p>
        </div>

        <Separator />

        {/* QR Code Data */}
        <div className="space-y-2">
          <Label htmlFor="qrCodeData">
            {translate('pos.settings.payment.qrCodeData', 'QR Code Data')}
            <span className="text-muted-foreground ml-2">
              ({translate('pos.settings.payment.or', 'or')}{' '}
              {translate(
                'pos.settings.payment.generateAutomatically',
                'generate automatically'
              )}
              )
            </span>
          </Label>
          <Textarea
            id="qrCodeData"
            placeholder={translate(
              'pos.settings.payment.qrCodeDataPlaceholder',
              'Enter payment information (e.g., bank account, e-wallet, payment link)'
            )}
            value={settings.qrCodeData}
            onChange={(e) => {
              handleSettingChange('qrCodeData', e.target.value);
              // Clear image when text data is entered
              if (e.target.value.trim() && imagePreview) {
                setImagePreview(null);
                handleSettingChange('qrCodeImage', undefined);
              }
            }}
            rows={4}
            className="font-mono text-sm"
            disabled={!!imagePreview}
          />
          <p className="text-sm text-muted-foreground">
            {translate(
              'pos.settings.payment.qrCodeDataHelp',
              'This data will be encoded into the QR code. Customers can scan it to make payments. Leave empty if you uploaded an image above.'
            )}
          </p>
        </div>

        {/* QR Code Label */}
        <div className="space-y-2">
          <Label htmlFor="qrCodeLabel">
            {translate('pos.settings.payment.qrCodeLabel', 'QR Code Label')}
          </Label>
          <Input
            id="qrCodeLabel"
            placeholder={translate(
              'pos.settings.payment.qrCodeLabelPlaceholder',
              'Scan to Pay'
            )}
            value={settings.qrCodeLabel || ''}
            onChange={(e) => handleSettingChange('qrCodeLabel', e.target.value)}
          />
          <p className="text-sm text-muted-foreground">
            {translate(
              'pos.settings.payment.qrCodeLabelHelp',
              'Optional label displayed above the QR code on receipts'
            )}
          </p>
        </div>

        {/* Include in Receipt Toggle */}
        <div className="flex items-center justify-between space-x-2">
          <div className="space-y-0.5">
            <Label htmlFor="includeInReceipt">
              {translate(
                'pos.settings.payment.includeInReceipt',
                'Include in Receipt'
              )}
            </Label>
            <p className="text-sm text-muted-foreground">
              {translate(
                'pos.settings.payment.includeInReceiptHelp',
                'Show QR code in the footer of printed receipts'
              )}
            </p>
          </div>
          <Switch
            id="includeInReceipt"
            checked={settings.includeInReceipt}
            onCheckedChange={(checked) =>
              handleSettingChange('includeInReceipt', checked)
            }
          />
        </div>

        <Separator />

        {/* Preview Section */}
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">
              {translate('pos.settings.payment.preview', 'Preview')}
            </h3>
            <Button
              variant="outline"
              size="sm"
              onClick={generateQRPreview}
              disabled={
                (!settings.qrCodeData.trim() && !imagePreview) || isGenerating
              }
            >
              {showQRPreview ? (
                <>
                  <EyeOff className="h-4 w-4 mr-2" />
                  {translate('pos.settings.payment.hidePreview', 'Hide')}
                </>
              ) : (
                <>
                  <Eye className="h-4 w-4 mr-2" />
                  {translate('pos.settings.payment.showPreview', 'Show')}
                </>
              )}
            </Button>
          </div>

          {showQRPreview && (
            <div className="border rounded-lg p-4 bg-muted/50 overflow-x-auto">
              <div
                className="receipt-preview"
                style={{
                  fontFamily: "'Courier New', monospace",
                  fontSize: '12px',
                  lineHeight: '1.4',
                  maxWidth: '80mm',
                  margin: '0 auto',
                  padding: '10mm',
                  backgroundColor: 'white',
                  minWidth: '300px',
                }}
              >
                {/* Receipt Header */}
                <div
                  style={{
                    textAlign: 'center',
                    marginBottom: '15px',
                    borderBottom: '1px dashed #000',
                    paddingBottom: '10px',
                  }}
                >
                  <div
                    style={{
                      fontSize: '16px',
                      fontWeight: 'bold',
                      marginBottom: '5px',
                    }}
                  >
                    NEKO COFFEE
                  </div>
                  <div style={{ fontSize: '10px' }}>Receipt</div>
                </div>

                {/* Sample Order Info */}
                <div style={{ margin: '10px 0', fontSize: '11px' }}>
                  <div>
                    <strong>Order:</strong> ORDER-2025-01-01-0001
                  </div>
                  <div>
                    <strong>Date:</strong>{' '}
                    {new Date().toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </div>
                </div>

                {/* Sample Items */}
                <div
                  style={{
                    margin: '15px 0',
                    borderTop: '1px dashed #000',
                    borderBottom: '1px dashed #000',
                    padding: '10px 0',
                  }}
                >
                  <div style={{ margin: '8px 0' }}>
                    <div style={{ fontWeight: 'bold' }}>Sample Product × 1</div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: '3px',
                        fontSize: '11px',
                      }}
                    >
                      <span>50,000₫ × 1</span>
                      <span>50,000₫</span>
                    </div>
                  </div>
                </div>

                {/* Sample Totals */}
                <div style={{ margin: '15px 0' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      margin: '5px 0',
                      fontSize: '11px',
                    }}
                  >
                    <span>Subtotal:</span>
                    <span>50,000₫</span>
                  </div>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      margin: '10px 0',
                      fontSize: '14px',
                      fontWeight: 'bold',
                      borderTop: '1px solid #000',
                      borderBottom: '1px solid #000',
                      padding: '8px 0',
                    }}
                  >
                    <span>TOTAL:</span>
                    <span>50,000₫</span>
                  </div>
                </div>

                {/* Receipt Footer with QR Code */}
                <div
                  style={{
                    textAlign: 'center',
                    marginTop: '20px',
                    paddingTop: '10px',
                    borderTop: '1px dashed #000',
                    fontSize: '10px',
                  }}
                >
                  <div>Thank you for your visit!</div>
                  <div style={{ marginTop: '5px' }}>Have a great day!</div>
                  {(imagePreview || previewQR) && (
                    <div
                      style={{
                        marginTop: '15px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '5px',
                      }}
                    >
                      {settings.qrCodeLabel && (
                        <div
                          style={{
                            fontWeight: 'bold',
                            fontSize: '11px',
                            marginBottom: '5px',
                          }}
                        >
                          {settings.qrCodeLabel}
                        </div>
                      )}
                      <div
                        style={{
                          background: 'white',
                          padding: '5px',
                          border: '1px solid #ddd',
                        }}
                      >
                        {imagePreview ? (
                          <img
                            src={imagePreview}
                            alt="QR Code Preview"
                            style={{
                              maxWidth: '150px',
                              maxHeight: '150px',
                              display: 'block',
                              filter: 'grayscale(100%)',
                            }}
                          />
                        ) : previewQR ? (
                          <div
                            dangerouslySetInnerHTML={{ __html: previewQR }}
                            style={{ width: '150px', height: '150px' }}
                          />
                        ) : null}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {showQRPreview &&
            !previewQR &&
            !imagePreview &&
            settings.qrCodeData && (
              <Alert>
                <AlertDescription>
                  {translate(
                    'pos.settings.payment.previewError',
                    'Unable to generate QR code preview. Please check your QR code data.'
                  )}
                </AlertDescription>
              </Alert>
            )}
        </div>
      </CardContent>
    </Card>
  );
};

export default PaymentSettings;







